import classnames from 'classnames'
import { Formik } from 'formik'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Button from '../../components/basics/Button/Button'
import { CheckboxFormik } from '../../components/basics/Checkbox/Checkbox'
import Menu from '../../components/basics/Menu/Menu'
import LearningItemTeaser from '../../components/LearningItemTeaser/LearningItemTeaser'
import SidebarExtension from '../../components/Page/SidebarExtension'
import Track from '../../components/Track/Track'
import allowedCourseFilterOptions from '../../state/calculations/permissions/allowedCourseFilterOptions'
import {
  userCanCreateLearningPath,
  userCanCreateOnDemandCourse,
  userCanCreateScheduledCourse,
} from '../../state/calculations/permissions/userCan'
import useLearningItemFilter from '../../state/queries/useLearningItemFilter'
import useLearningItemFilterOptions from '../../state/queries/useLearningItemFilterOptions'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import styles from './CoursesPage.module.scss'

export default React.memo(function CoursesPage() {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()

  const filterOptions = useLearningItemFilterOptions()

  const learningItemFilter = useLearningItemFilter()

  const [filtersHidden, setFiltersVisible] = useState(false)
  const handleFiltersVisibilityToggled = useCallback(() => setFiltersVisible((prev) => !prev), [setFiltersVisible])

  const resetFilters = useCallback(
    () => learningItemFilter.setFilter({ favorites: false, categories: [], type: [] }),
    [learningItemFilter.setFilter]
  )

  const onSortClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget.dataset.sortType) {
        learningItemFilter.setSort(e.currentTarget.dataset.sortType)
      }
    },
    [learningItemFilter.setSort]
  )

  const allowedFilterOptions = useMemo(() => allowedCourseFilterOptions(currentUser), [currentUser])

  const createOptions = [
    userCanCreateScheduledCourse(currentUser) && (
      <Button key="create scheduled course" menuItem component={Link} to={routes.createScheduledCourse.url({})}>
        {t('edit learning.cta create scheduled course')}
      </Button>
    ),
    userCanCreateOnDemandCourse(currentUser) && (
      <Button key="cta create on demand course" menuItem component={Link} to={routes.createOnDemandCourse.url({})}>
        {t('edit learning.cta create on demand course')}
      </Button>
    ),
    userCanCreateLearningPath(currentUser) && (
      <Button key="cta create learning path" menuItem component={Link} to={routes.createLearningPath.url({})}>
        {t('edit learning.cta create learning path')}
      </Button>
    ),
  ].filter(Boolean)

  const sortingTypeTranslated =
    learningItemFilter.sort === 'start_date' || learningItemFilter.sort === null
      ? t('edit learning.cta sort by start date')
      : learningItemFilter.sort === 'alphabetically'
      ? t('edit learning.cta sort alphabetically')
      : t('edit learning.cta sort by created at')

  return (
    <>
      <SidebarExtension>
        <div className={classnames(styles.filtersWrapper, filtersHidden && styles.closed)}>
          <Formik
            initialValues={{
              categories: learningItemFilter.categories,
              favorites: learningItemFilter.favorites,
              type: learningItemFilter.type,
              status: learningItemFilter.status,
            }}
            enableReinitialize={true}
            onSubmit={async (values) => learningItemFilter.setFilter(values)}
          >
            {(props) => (
              <form noValidate onSubmit={props.handleSubmit} onReset={props.handleReset} className={styles.filters}>
                {allowedFilterOptions.favorites && (
                  <div role="list" aria-label={t('browse courses.wishlist')}>
                    <h2 aria-hidden>{t('browse courses.wishlist')}</h2>
                    <CheckboxFormik omitErrorDisplay name="favorites" label={t('browse courses.only favorites')} />
                  </div>
                )}

                {allowedFilterOptions.type && (
                  <div
                    role="list"
                    aria-label={t('learning item.learning format')}
                    className={classnames({ skeleton: filterOptions.isPlaceholderData })}
                  >
                    <h2 aria-hidden>{t('learning item.learning format')}</h2>
                    {filterOptions.type.map(({ name, id }) => (
                      <CheckboxFormik
                        omitErrorDisplay
                        key={id}
                        name="type"
                        label={t(`learning item.type.${name}`)}
                        value={id}
                      />
                    ))}
                  </div>
                )}

                {allowedFilterOptions.categories && (
                  <div
                    role="list"
                    aria-label={t('learning item.categories')}
                    className={classnames({ skeleton: filterOptions.isPlaceholderData })}
                  >
                    <h2 aria-hidden>{t('learning item.categories')}</h2>
                    {filterOptions.category.map(({ uuid, name }) => (
                      <CheckboxFormik omitErrorDisplay key={uuid} name="categories" label={name} value={uuid} />
                    ))}
                  </div>
                )}

                {(allowedFilterOptions.status.draft || allowedFilterOptions.status.deprecated) && (
                  <div
                    role="list"
                    aria-label={t('learning item.field status')}
                    className={classnames({ skeleton: filterOptions.isPlaceholderData })}
                  >
                    <h2 aria-hidden>{t('learning item.field status')}</h2>
                    {allowedFilterOptions.status.draft && (
                      <CheckboxFormik
                        omitErrorDisplay
                        name="status"
                        label={t('learning item.status.draft')}
                        value="draft"
                      />
                    )}
                    {allowedFilterOptions.status.published && (
                      <CheckboxFormik
                        omitErrorDisplay
                        name="status"
                        label={t('learning item.status.published')}
                        value="published"
                      />
                    )}
                    {allowedFilterOptions.status.deprecated && (
                      <CheckboxFormik
                        omitErrorDisplay
                        name="status"
                        label={t('learning item.status.deprecated')}
                        value="deprecated"
                      />
                    )}
                  </div>
                )}

                <Button type="submit" filled className={styles.applyFilterButton}>
                  {t('browse courses.cta show results')}
                </Button>

                <button type="reset" onClick={resetFilters} className={styles.clearFilterButton}>
                  {t('browse courses.cta reset filters')}
                </button>
              </form>
            )}
          </Formik>
        </div>
        <button aria-hidden onClick={handleFiltersVisibilityToggled} className={styles.toggleFilterButton}>
          {filtersHidden ? t('browse courses.cta show filters') : t('browse courses.cta hide filters')}
        </button>
      </SidebarExtension>

      <div className={styles.CoursesPage}>
        <Track
          className={styles.courseTrack}
          header={
            <>
              <h1>{t('browse courses.title courses')}</h1>

              <Menu small filled label={t('edit learning.sort learning items', { type: sortingTypeTranslated })}>
                <Button menuItem onClick={onSortClick} data-sort-type="start_date">
                  {t('edit learning.cta sort by start date')}
                </Button>
                <Button menuItem onClick={onSortClick} data-sort-type="alphabetically">
                  {t('edit learning.cta sort alphabetically')}
                </Button>
                <Button menuItem onClick={onSortClick} data-sort-type="created_at">
                  {t('edit learning.cta sort by created at')}
                </Button>
              </Menu>

              {createOptions.length > 0 && (
                <Menu small filled label={t('edit learning.cta create learning item')}>
                  {createOptions}
                </Menu>
              )}
            </>
          }
          isLoading={learningItemFilter.result.isFetching && !learningItemFilter.result.isFetchingNextPage}
          noItemsHint={t('browse courses.courses empty')}
          footer={
            learningItemFilter.hasNextPage && (
              <div className={styles.loadMoreButton}>
                <Button loading={learningItemFilter.isFetching} onClick={() => learningItemFilter.fetchNextPage()}>
                  Load more
                </Button>
              </div>
            )
          }
        >
          {learningItemFilter.learningItems &&
            learningItemFilter.learningItems.map((learningItem) => (
              <LearningItemTeaser key={learningItem.uuid} learningItem={learningItem} />
            ))}
        </Track>
      </div>
    </>
  )
})

export function CoursesPageBreadcrumb() {
  const { t } = useTranslation()
  const routes = useRoutes()
  return <Link to={routes.courses.url({})}>{t('browse courses.page title')}</Link>
}
